// Hook for Gantt CRUD operations with multi-user support
import { useCallback, useState } from 'react';
import { 
  GanttTask, 
  GanttDependency, 
  TaskFormData,
  ProjectFormData
} from '../types/gantt.types';
import { ganttService } from '../services/ganttService';
import { useToast } from '../../../hooks/useToast';

export const useGanttActions = (onDataChange?: () => void) => {
  const { showToast } = useToast();
  const [isLocking, setIsLocking] = useState(false);

  // ==================== PROJECTS ====================
  const createProject = useCallback(async (project: ProjectFormData) => {
    try {
      const newProject = await ganttService.createProject(project);
      showToast('Projekt utworzony', 'success');
      onDataChange?.();
      return newProject;
    } catch (error) {
      showToast('Błąd przy tworzeniu projektu', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const updateProject = useCallback(async (projectId: string, updates: Partial<ProjectFormData>) => {
    try {
      const updatedProject = await ganttService.updateProject(projectId, updates);
      showToast('Projekt zaktualizowany', 'success');
      onDataChange?.();
      return updatedProject;
    } catch (error) {
      showToast('Błąd przy aktualizacji projektu', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const deleteProject = useCallback(async (projectId: string) => {
    try {
      await ganttService.deleteProject(projectId);
      showToast('Projekt usunięty', 'success');
      onDataChange?.();
    } catch (error) {
      showToast('Błąd przy usuwaniu projektu', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  // ==================== TASKS WITH LOCKING ====================
  const createTask = useCallback(async (task: TaskFormData) => {
    try {
      const newTask = await ganttService.createTask(task);
      showToast('Zadanie utworzone', 'success');
      onDataChange?.();
      return newTask;
    } catch (error) {
      showToast('Błąd przy tworzeniu zadania', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const updateTask = useCallback(async (taskId: string, updates: Partial<GanttTask>) => {
    try {
      // Try to lock the task first
      setIsLocking(true);
      const locked = await ganttService.lockTask(taskId);
      
      if (!locked) {
        showToast('Zadanie jest edytowane przez innego użytkownika', 'warning');
        return null;
      }

      // Update the task
      const updatedTask = await ganttService.updateTask(taskId, updates);
      
      // Unlock after successful update
      await ganttService.unlockTask(taskId);
      
      showToast('Zadanie zaktualizowane', 'success');
      onDataChange?.();
      return updatedTask;
    } catch (error: any) {
      // Try to unlock on error
      await ganttService.unlockTask(taskId);
      
      if (error.message?.includes('locked by')) {
        showToast(error.message, 'warning');
      } else {
        showToast('Błąd przy aktualizacji zadania', 'error');
      }
      throw error;
    } finally {
      setIsLocking(false);
    }
  }, [showToast, onDataChange]);

  const deleteTask = useCallback(async (taskId: string) => {
    try {
      // Try to lock before delete
      const locked = await ganttService.lockTask(taskId);
      
      if (!locked) {
        showToast('Nie można usunąć - zadanie jest edytowane przez innego użytkownika', 'warning');
        return;
      }

      await ganttService.deleteTask(taskId);
      showToast('Zadanie usunięte', 'success');
      onDataChange?.();
    } catch (error) {
      showToast('Błąd przy usuwaniu zadania', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  // ==================== QUICK ACTIONS WITH LOCKING ====================
  const updateTaskProgress = useCallback(async (taskId: string, progress: number) => {
    try {
      const locked = await ganttService.lockTask(taskId);
      if (!locked) {
        showToast('Zadanie jest edytowane przez innego użytkownika', 'warning');
        return null;
      }

      const updatedTask = await ganttService.updateTask(taskId, { progress });
      await ganttService.unlockTask(taskId);
      
      onDataChange?.();
      return updatedTask;
    } catch (error) {
      await ganttService.unlockTask(taskId);
      showToast('Błąd przy aktualizacji postępu', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const moveTask = useCallback(async (taskId: string, newStartDate: Date, newEndDate: Date) => {
    try {
      const locked = await ganttService.lockTask(taskId);
      if (!locked) {
        showToast('Zadanie jest edytowane przez innego użytkownika', 'warning');
        return null;
      }

      const updatedTask = await ganttService.updateTask(taskId, {
        start_date: newStartDate,
        end_date: newEndDate
      });
      
      await ganttService.unlockTask(taskId);
      onDataChange?.();
      return updatedTask;
    } catch (error) {
      await ganttService.unlockTask(taskId);
      showToast('Błąd przy przesuwaniu zadania', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const resizeTask = useCallback(async (taskId: string, newEndDate: Date) => {
    try {
      const locked = await ganttService.lockTask(taskId);
      if (!locked) {
        showToast('Zadanie jest edytowane przez innego użytkownika', 'warning');
        return null;
      }

      const updatedTask = await ganttService.updateTask(taskId, {
        end_date: newEndDate
      });
      
      await ganttService.unlockTask(taskId);
      onDataChange?.();
      return updatedTask;
    } catch (error) {
      await ganttService.unlockTask(taskId);
      showToast('Błąd przy zmianie czasu trwania', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  // ==================== DEPENDENCIES ====================
  const createDependency = useCallback(async (
    sourceId: string,
    targetId: string,
    type: 'FS' | 'FF' | 'SF' | 'SS' = 'FS'
  ) => {
    try {
      const dependency = await ganttService.createDependency({
        source_id: sourceId,
        target_id: targetId,
        type,
        lag: 0
      });
      showToast('Zależność utworzona', 'success');
      onDataChange?.();
      return dependency;
    } catch (error) {
      showToast('Błąd przy tworzeniu zależności', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const deleteDependency = useCallback(async (dependencyId: string) => {
    try {
      await ganttService.deleteDependency(dependencyId);
      showToast('Zależność usunięta', 'success');
      onDataChange?.();
    } catch (error) {
      showToast('Błąd przy usuwaniu zależności', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  // ==================== RESOURCES ====================
  const assignResource = useCallback(async (taskId: string, resource: any) => {
    try {
      const locked = await ganttService.lockTask(taskId);
      if (!locked) {
        showToast('Zadanie jest edytowane przez innego użytkownika', 'warning');
        return null;
      }

      const newResource = await ganttService.createResource({
        task_id: taskId,
        ...resource
      });
      
      await ganttService.unlockTask(taskId);
      showToast('Zasób przypisany', 'success');
      onDataChange?.();
      return newResource;
    } catch (error) {
      await ganttService.unlockTask(taskId);
      showToast('Błąd przy przypisywaniu zasobu', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  const removeResource = useCallback(async (resourceId: string) => {
    try {
      await ganttService.deleteResource(resourceId);
      showToast('Zasób usunięty', 'success');
      onDataChange?.();
    } catch (error) {
      showToast('Błąd przy usuwaniu zasobu', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  // ==================== BATCH OPERATIONS ====================
  const reorderTasks = useCallback(async (taskIds: string[]) => {
    try {
      await ganttService.reorderTasks(taskIds);
      onDataChange?.();
    } catch (error) {
      showToast('Błąd przy zmianie kolejności zadań', 'error');
      throw error;
    }
  }, [showToast, onDataChange]);

  // ==================== LOCKING ====================
  const lockTaskForEdit = useCallback(async (taskId: string) => {
    try {
      const locked = await ganttService.lockTask(taskId);
      if (!locked) {
        showToast('Zadanie jest już edytowane przez innego użytkownika', 'warning');
      }
      return locked;
    } catch (error) {
      console.error('Error locking task:', error);
      return false;
    }
  }, [showToast]);

  const unlockTask = useCallback(async (taskId: string) => {
    try {
      await ganttService.unlockTask(taskId);
    } catch (error) {
      console.error('Error unlocking task:', error);
    }
  }, []);

  return {
    // Projects
    createProject,
    updateProject,
    deleteProject,
    
    // Tasks
    createTask,
    updateTask,
    deleteTask,
    updateTaskProgress,
    moveTask,
    resizeTask,
    
    // Dependencies
    createDependency,
    deleteDependency,
    
    // Resources
    assignResource,
    removeResource,
    
    // Batch
    reorderTasks,
    
    // Locking
    lockTaskForEdit,
    unlockTask,
    isLocking
  };
};